import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Megaphone, Loader2, Send, Users, CheckCircle2, AlertTriangle, FlaskConical,
  Pencil, X as XIcon, Plus, Save, Star, History, Copy,
  CalendarClock, Eye, MousePointerClick, AlertCircle, Trash2, Filter, UserCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';

const emptySection = () => ({ emoji: '✨', title: '', body: '', featured: false });

export default function AnnouncementCampaignCard({ API }) {
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [originalDraft, setOriginalDraft] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({}); // {campaign_id: {open_rate, click_rate, bounce_rate, ...}}
  const [scheduled, setScheduled] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // datetime-local default = next Monday 9:00 AM in admin's local tz
  const [scheduleLocal, setScheduleLocal] = useState(() => {
    const d = new Date();
    const daysUntilMon = (8 - d.getDay()) % 7 || 7; // always strictly future Monday
    d.setDate(d.getDate() + daysUntilMon);
    d.setHours(9, 0, 0, 0);
    // format yyyy-MM-ddTHH:mm for datetime-local
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [result, setResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  // 1:1 outreach
  const [singleEmail, setSingleEmail] = useState('');
  const [sendingSingle, setSendingSingle] = useState(false);

  // Audience filter — when both blank, no filter is sent.
  const [audience, setAudience] = useState({ joined_after: '', joined_before: '' });
  const audienceActive = !!(audience.joined_after || audience.joined_before);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`
  });

  const previewWithAudience = async (aud) => {
    const params = new URLSearchParams();
    if (aud?.joined_after) params.set('joined_after', aud.joined_after);
    if (aud?.joined_before) params.set('joined_before', aud.joined_before);
    const qs = params.toString();
    const url = `${API}/admin/announcement/preview${qs ? `?${qs}` : ''}`;
    const r = await axios.get(url, { headers: authHeaders() });
    return r.data;
  };

  // Refetch preview (eligible count) whenever the audience filter changes.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const data = await previewWithAudience(audience);
        if (!cancelled) setPreview(data);
      } catch (e) { /* noop */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [audience.joined_after, audience.joined_before]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, d, c, s] = await Promise.all([
        axios.get(`${API}/admin/announcement/preview`, { headers: authHeaders() }),
        axios.get(`${API}/admin/announcement/draft`, { headers: authHeaders() }),
        axios.get(`${API}/admin/announcement/campaigns`, { headers: authHeaders() }),
        axios.get(`${API}/admin/announcement/scheduled`, { headers: authHeaders() })
      ]);
      setPreview(p.data);
      setDraft(d.data.draft);
      setOriginalDraft(JSON.parse(JSON.stringify(d.data.draft)));
      const camps = c.data.campaigns || [];
      setCampaigns(camps);
      setScheduled(s.data.scheduled || []);
      // Fetch stats for every past campaign in parallel — best effort, ignore errors
      const statsResults = await Promise.allSettled(
        camps.map(cc =>
          axios.get(`${API}/admin/announcement/campaigns/${cc.id}/stats`, { headers: authHeaders() })
            .then(r => [cc.id, r.data])
        )
      );
      const statsMap = {};
      for (const sr of statsResults) {
        if (sr.status === 'fulfilled') statsMap[sr.value[0]] = sr.value[1];
      }
      setCampaignStats(statsMap);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load announcement info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = draft && originalDraft && JSON.stringify(draft) !== JSON.stringify(originalDraft);

  const saveDraft = async () => {
    if (!draft) return;
    setSavingDraft(true);
    try {
      await axios.put(`${API}/admin/announcement/draft`, draft, { headers: authHeaders() });
      setOriginalDraft(JSON.parse(JSON.stringify(draft)));
      toast.success('Draft saved');
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    } finally {
      setSavingDraft(false);
    }
  };

  const discardChanges = () => {
    setDraft(JSON.parse(JSON.stringify(originalDraft)));
  };

  const loadAsTemplate = async (campaignId) => {
    if (dirty && !window.confirm('You have unsaved draft changes. Loading a template will discard them. Continue?')) return;
    setLoadingTemplate(campaignId);
    try {
      const { data } = await axios.post(
        `${API}/admin/announcement/draft/from-campaign/${campaignId}`,
        {},
        { headers: authHeaders() }
      );
      setDraft(data.draft);
      setOriginalDraft(JSON.parse(JSON.stringify(data.draft)));
      setEditing(true);
      toast.success('Template loaded — edit and save when ready');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Could not load template');
    } finally {
      setLoadingTemplate(null);
    }
  };

  const updateField = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));
  const updateSection = (idx, key, val) => setDraft(prev => ({
    ...prev,
    sections: prev.sections.map((s, i) => i === idx ? { ...s, [key]: val } : s)
  }));
  const addSection = () => setDraft(prev => ({ ...prev, sections: [...prev.sections, emptySection()] }));
  const removeSection = (idx) => setDraft(prev => ({
    ...prev,
    sections: prev.sections.filter((_, i) => i !== idx)
  }));
  const moveSection = (idx, dir) => setDraft(prev => {
    const arr = [...prev.sections];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...prev, sections: arr };
  });

  const sendCampaign = async () => {
    if (dirty) {
      toast.error('You have unsaved draft changes. Save or discard first.');
      return;
    }
    setSending(true);
    try {
      const body = audienceActive ? { audience } : {};
      const { data } = await axios.post(`${API}/admin/announcement/send`, body, { headers: authHeaders() });
      setResult(data);
      toast.success(`Announcement sent: ${data.sent} emails delivered`);
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error('Announcement failed to send');
    } finally {
      setSending(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (dirty) {
      toast.error('Save your draft changes first');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API}/admin/announcement/send-test`, { to_email: testEmail }, { headers: authHeaders() });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      console.error(e);
      toast.error('Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const sendToSingleUser = async () => {
    if (!singleEmail || !singleEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (dirty) {
      toast.error('Save your draft changes first');
      return;
    }
    if (!window.confirm(`Send the current announcement to ${singleEmail}? This is real outreach, not a test.`)) return;
    setSendingSingle(true);
    try {
      const r = await axios.post(
        `${API}/admin/announcement/send-to-user`,
        { to_email: singleEmail },
        { headers: authHeaders() }
      );
      toast.success(`Sent to ${r.data.personalized_as || singleEmail}`);
      setSingleEmail('');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSendingSingle(false);
    }
  };

  const scheduleCampaign = async () => {
    if (dirty) {
      toast.error('Save your draft changes first');
      return;
    }
    // Parse the datetime-local value as local time, convert to UTC ISO
    const localDate = new Date(scheduleLocal);
    if (isNaN(localDate.getTime())) {
      toast.error('Pick a valid date/time');
      return;
    }
    if (localDate.getTime() <= Date.now()) {
      toast.error('Pick a future date/time');
      return;
    }
    setScheduling(true);
    try {
      const body = { scheduled_for: localDate.toISOString() };
      if (audienceActive) body.audience = audience;
      await axios.post(
        `${API}/admin/announcement/schedule`,
        body,
        { headers: authHeaders() }
      );
      toast.success(`Queued for ${localDate.toLocaleString()}`);
      setScheduleOpen(false);
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally {
      setScheduling(false);
    }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm('Cancel this scheduled broadcast?')) return;
    try {
      await axios.delete(`${API}/admin/announcement/scheduled/${id}`, { headers: authHeaders() });
      toast.success('Cancelled');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 rounded-2xl p-4 sm:p-6 overflow-hidden" data-testid="announcement-campaign-card">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <Megaphone className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-[#1A1A1A]">"What's New" Announcement</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(v => !v)}
              className="border-indigo-300"
              data-testid="toggle-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {editing ? 'Hide editor' : 'Edit content'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Reusable broadcast tool. Edit the content below, save, send a test to yourself, then broadcast.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : preview ? (
            <>
              {/* Editor */}
              {editing && draft && (
                <div className="bg-white rounded-xl p-4 border border-indigo-200 mb-4 space-y-4" data-testid="announcement-editor">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Subject line</label>
                    <input
                      value={draft.subject}
                      onChange={(e) => updateField('subject', e.target.value)}
                      className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-400"
                      data-testid="draft-subject-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Headline (top of email)</label>
                    <input
                      value={draft.headline}
                      onChange={(e) => updateField('headline', e.target.value)}
                      className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Intro paragraph <span className="font-normal italic">(use <code className="bg-muted px-1 rounded">{'{first_name}'}</code> to personalize)</span></label>
                    <textarea
                      value={draft.intro}
                      onChange={(e) => updateField('intro', e.target.value)}
                      rows={4}
                      className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-400 resize-y leading-relaxed"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-muted-foreground">Sections ({draft.sections.length})</label>
                      <Button size="sm" variant="outline" onClick={addSection} className="border-indigo-300 h-7 text-xs" data-testid="add-section-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add section
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {draft.sections.map((s, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${s.featured ? 'bg-emerald-50 border-emerald-200' : 'bg-muted/30 border-border'}`} data-testid={`section-row-${idx}`}>
                          <div className="flex items-start gap-2">
                            <input
                              value={s.emoji}
                              onChange={(e) => updateSection(idx, 'emoji', e.target.value)}
                              maxLength={4}
                              className="w-12 text-center text-lg px-1 py-1.5 border border-indigo-200 rounded-md focus:outline-none"
                              placeholder="✨"
                            />
                            <div className="flex-1 space-y-2">
                              <input
                                value={s.title}
                                onChange={(e) => updateSection(idx, 'title', e.target.value)}
                                placeholder="Section title"
                                className="w-full text-sm font-medium px-2 py-1.5 border border-indigo-200 rounded-md focus:outline-none"
                              />
                              <textarea
                                value={s.body}
                                onChange={(e) => updateSection(idx, 'body', e.target.value)}
                                placeholder="Section description (1–2 sentences)"
                                rows={3}
                                className="w-full text-xs px-2 py-1.5 border border-indigo-200 rounded-md focus:outline-none resize-y leading-relaxed"
                              />
                              <div className="flex items-center gap-3 text-xs">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!s.featured}
                                    onChange={(e) => updateSection(idx, 'featured', e.target.checked)}
                                    data-testid={`section-featured-${idx}`}
                                  />
                                  <Star className="w-3 h-3 text-emerald-600" /> Featured tile
                                </label>
                                <span className="text-muted-foreground/70 text-[10px] italic">(featured + "App Store" auto-attaches store badges)</span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <button type="button" disabled={idx === 0} onClick={() => moveSection(idx, -1)} className="text-xs text-muted-foreground hover:text-indigo-700 disabled:opacity-30">↑</button>
                              <button type="button" disabled={idx === draft.sections.length - 1} onClick={() => moveSection(idx, 1)} className="text-xs text-muted-foreground hover:text-indigo-700 disabled:opacity-30">↓</button>
                              <button type="button" onClick={() => removeSection(idx)} className="text-xs text-red-500 hover:text-red-700" data-testid={`section-remove-${idx}`}>
                                <XIcon className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">CTA label</label>
                      <input value={draft.cta_label} onChange={(e) => updateField('cta_label', e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">CTA URL</label>
                      <input value={draft.cta_url} onChange={(e) => updateField('cta_url', e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Signoff</label>
                    <input value={draft.signoff} onChange={(e) => updateField('signoff', e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg" />
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-indigo-100">
                    {dirty && (
                      <Button variant="ghost" size="sm" onClick={discardChanges} className="text-muted-foreground">Discard</Button>
                    )}
                    <Button
                      size="sm"
                      onClick={saveDraft}
                      disabled={!dirty || savingDraft}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="save-draft-btn"
                    >
                      {savingDraft ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                      {savingDraft ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
                <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-indigo-100 min-w-0">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
                    <Users className="w-3 h-3" /> Eligible
                  </div>
                  <p className="text-xl font-bold text-indigo-700" data-testid="announcement-eligible-count">{preview.eligible_users}</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-indigo-100 min-w-0">
                  <div className="text-[11px] text-muted-foreground mb-1">Total users</div>
                  <p className="text-xl font-bold text-[#1A1A1A]">{preview.total_users}</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-indigo-100 min-w-0 col-span-2 sm:col-span-1">
                  <div className="text-[11px] text-muted-foreground mb-1">Unsubscribed</div>
                  <p className="text-xl font-bold text-muted-foreground">{preview.unsubscribed}</p>
                </div>
              </div>

              {preview.last_campaign && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4" data-testid="last-announcement-info">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  Last sent: <span className="font-medium text-foreground">{formatDate(preview.last_campaign.started_at)}</span>
                  {' · '}delivered to {preview.last_campaign.sent} user{preview.last_campaign.sent !== 1 ? 's' : ''}
                  {preview.last_campaign.failed > 0 && ` · ${preview.last_campaign.failed} failed`}
                </div>
              )}

              {/* Upcoming scheduled broadcasts */}
              {scheduled.filter(s => s.status === 'scheduled').length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl mb-4 overflow-hidden" data-testid="scheduled-broadcasts-section">
                  <div className="px-3 py-2 bg-amber-100/60 border-b border-amber-200 flex items-center gap-1.5">
                    <CalendarClock className="w-3.5 h-3.5 text-amber-700" />
                    <p className="text-xs font-semibold text-amber-900">
                      Upcoming ({scheduled.filter(s => s.status === 'scheduled').length})
                    </p>
                  </div>
                  <div className="divide-y divide-amber-100">
                    {scheduled.filter(s => s.status === 'scheduled').map(s => (
                      <div key={s.id} className="px-3 py-2 flex items-center gap-3" data-testid={`scheduled-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#1A1A1A] truncate">{s.subject || '(no subject)'}</p>
                          <p className="text-[10px] text-amber-700">
                            Fires {formatDate(s.scheduled_for)}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelScheduled(s.id)}
                          className="border-amber-300 text-amber-800 hover:bg-amber-100 h-7 text-[11px] px-2 flex-shrink-0"
                          data-testid={`cancel-scheduled-${s.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Past campaigns — load as template */}
              {campaigns.length > 0 && (
                <div className="bg-white rounded-xl border border-indigo-100 mb-4 overflow-hidden" data-testid="past-campaigns-section">
                  <div className="px-3 py-2 bg-indigo-50/50 border-b border-indigo-100 flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5 text-indigo-700" />
                    <p className="text-xs font-semibold text-[#1A1A1A]">Past campaigns ({campaigns.length})</p>
                  </div>
                  <div className="max-h-56 overflow-y-auto divide-y divide-indigo-50">
                    {campaigns.map((c) => {
                      const st = campaignStats[c.id];
                      return (
                        <div key={c.id} className="px-3 py-2 flex items-center gap-3" data-testid={`past-campaign-${c.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-[#1A1A1A] truncate">{c.subject || '(no subject)'}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {formatDate(c.started_at)} · {c.sent_count} sent
                              {c.failed_count > 0 && ` · ${c.failed_count} failed`}
                              {c.status === 'running' && ' · running…'}
                              {c.source === 'scheduled' && ' · scheduled'}
                            </p>
                            {st && st.has_data && (
                              <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid={`stats-${c.id}`}>
                                <span className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                                  <Eye className="w-2.5 h-2.5" /> {st.open_rate}% opened
                                </span>
                                <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full">
                                  <MousePointerClick className="w-2.5 h-2.5" /> {st.click_rate}% clicked
                                </span>
                                {st.bounce_rate > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full">
                                    <AlertCircle className="w-2.5 h-2.5" /> {st.bounce_rate}% bounced
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => loadAsTemplate(c.id)}
                            disabled={!c.has_snapshot || loadingTemplate === c.id}
                            className="border-indigo-300 h-7 text-[11px] px-2 flex-shrink-0"
                            title={c.has_snapshot ? 'Load this campaign as a starting template' : 'No snapshot stored for this older campaign'}
                            data-testid={`use-template-btn-${c.id}`}
                          >
                            {loadingTemplate === c.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <><Copy className="w-3 h-3 mr-1" /> Use as template</>
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Audience filter */}
              <div className="bg-white border border-indigo-100 rounded-lg p-3 mb-4" data-testid="audience-filter-section">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-semibold text-[#1A1A1A] flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5 text-indigo-600" /> Audience
                  </p>
                  {audienceActive && (
                    <button
                      type="button"
                      onClick={() => setAudience({ joined_after: '', joined_before: '' })}
                      className="text-[11px] text-indigo-700 hover:underline"
                      data-testid="audience-clear-btn"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-muted-foreground">Joined on/after</label>
                    <input
                      type="date"
                      value={audience.joined_after}
                      onChange={(e) => setAudience(a => ({ ...a, joined_after: e.target.value }))}
                      className="w-full mt-0.5 text-sm px-2 py-1.5 border border-indigo-200 rounded-md focus:outline-none focus:border-indigo-400"
                      data-testid="audience-joined-after"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground">Joined on/before</label>
                    <input
                      type="date"
                      value={audience.joined_before}
                      onChange={(e) => setAudience(a => ({ ...a, joined_before: e.target.value }))}
                      className="w-full mt-0.5 text-sm px-2 py-1.5 border border-indigo-200 rounded-md focus:outline-none focus:border-indigo-400"
                      data-testid="audience-joined-before"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  {audienceActive ? (
                    <>Targeting <span className="font-semibold text-indigo-700">{preview.eligible_users}</span> of {preview.grand_total_users ?? preview.total_users} users (filtered by signup date).</>
                  ) : (
                    <>Leave both blank to send to all eligible users ({preview.eligible_users}).</>
                  )}
                </p>
              </div>

              {/* Test-send row */}
              <div className="bg-white border border-indigo-100 rounded-lg p-3 mb-4">
                <p className="text-xs font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1.5">
                  <FlaskConical className="w-3.5 h-3.5 text-indigo-600" /> Test the email first
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1 min-w-0 text-sm px-3 py-2 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-400"
                    data-testid="test-email-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={sendTestEmail}
                    disabled={sendingTest || !testEmail || dirty}
                    className="border-indigo-300 w-full sm:w-auto shrink-0"
                    data-testid="send-test-btn"
                  >
                    {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send test'}
                  </Button>
                </div>
                {dirty && <p className="text-[11px] text-amber-700 mt-1.5">Save your draft changes before testing.</p>}
              </div>

              {/* Send to one specific user (1:1 outreach) */}
              <div className="bg-white border border-emerald-200 rounded-lg p-3 mb-4" data-testid="single-user-section">
                <p className="text-xs font-semibold text-[#1A1A1A] mb-1 flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-600" /> Send to one specific user
                </p>
                <p className="text-[11px] text-muted-foreground mb-2">
                  Real 1:1 outreach (not a test). Email must belong to a Vitala user.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={singleEmail}
                    onChange={(e) => setSingleEmail(e.target.value)}
                    className="flex-1 min-w-0 text-sm px-3 py-2 border border-emerald-200 rounded-lg focus:outline-none focus:border-emerald-400"
                    data-testid="single-user-email-input"
                  />
                  <Button
                    size="sm"
                    onClick={sendToSingleUser}
                    disabled={sendingSingle || !singleEmail || dirty}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto shrink-0"
                    data-testid="single-user-send-btn"
                  >
                    {sendingSingle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send to this user'}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={preview.eligible_users === 0 || dirty}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto h-auto py-2.5 whitespace-normal text-left leading-snug"
                  data-testid="send-announcement-btn"
                >
                  <Send className="w-4 h-4 mr-2 shrink-0" />
                  <span>Broadcast to {preview.eligible_users} User{preview.eligible_users !== 1 ? 's' : ''}</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setScheduleOpen(true)}
                  disabled={preview.eligible_users === 0 || dirty}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 w-full sm:w-auto h-auto py-2.5 whitespace-normal text-left leading-snug"
                  data-testid="schedule-announcement-btn"
                >
                  <CalendarClock className="w-4 h-4 mr-2 shrink-0" />
                  <span>Schedule for later</span>
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="announcement-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-indigo-600" />
              Broadcast to {preview?.eligible_users} users?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will email <span className="font-semibold text-foreground">{preview?.eligible_users}</span> users
              with subject: <span className="italic">"{draft?.subject}"</span>. Did you send yourself a test first?
            </DialogDescription>
          </DialogHeader>

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800">✓ Broadcast complete</p>
              <p className="text-green-700 text-xs mt-1">
                Sent: {result.sent} · Failed: {result.failed} · Skipped: {result.skipped}
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
            <Button
              onClick={sendCampaign}
              disabled={sending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-send-announcement-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? 'Sending…' : 'Yes, broadcast now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-indigo-600" />
              Schedule broadcast
            </DialogTitle>
            <DialogDescription className="pt-2">
              Snapshot the current draft and queue it to send to{' '}
              <span className="font-semibold text-foreground">{preview?.eligible_users} users</span> at the time below
              (your local time zone).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Send at</label>
              <input
                type="datetime-local"
                value={scheduleLocal}
                onChange={(e) => setScheduleLocal(e.target.value)}
                className="w-full mt-1 text-sm px-3 py-2 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-400"
                data-testid="schedule-datetime-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Default is next Monday 9:00 AM. Job fires within ~60 seconds of the chosen time.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <p className="text-[11px] text-amber-800">
                The current draft is snapshotted now — later edits won't change what gets sent. You can cancel
                from the Upcoming list before it fires.
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setScheduleOpen(false)} disabled={scheduling}>Cancel</Button>
            <Button
              onClick={scheduleCampaign}
              disabled={scheduling}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-schedule-btn"
            >
              {scheduling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
              {scheduling ? 'Queuing…' : 'Queue broadcast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
